import {Location} from '@angular/common';
import {Inject, Injectable} from '@angular/core';
import {ActivatedRouteSnapshot, Router, RouterStateSnapshot, UrlTree} from '@angular/router';
import {MSAL_GUARD_CONFIG, MsalBroadcastService, MsalGuard, MsalGuardConfiguration, MsalService} from '@azure/msal-angular';
import {EventMessage, EventType} from '@azure/msal-browser';
import {filter, Observable, of} from 'rxjs';

@Injectable()
export class ExtendedMsalGuard extends MsalGuard {
    private static readonly POST_LOGIN_URL_KEY = 'msal.postLoginRedirectUrl';

    constructor(
        @Inject(MSAL_GUARD_CONFIG)
        msalGuardConfig: MsalGuardConfiguration,
        msalBroadcastService: MsalBroadcastService,
        private readonly msalService: MsalService,
        location: Location,
        private readonly appRouter: Router,
    ) {
        super(msalGuardConfig, msalBroadcastService, msalService, location, appRouter);

        msalBroadcastService.msalSubject$
            .pipe(
                filter((msg: EventMessage) =>
                    msg.eventType === EventType.LOGIN_SUCCESS || msg.eventType === EventType.ACQUIRE_TOKEN_SUCCESS,
                ),
            )
            .subscribe(() => {
                const saved = sessionStorage.getItem(ExtendedMsalGuard.POST_LOGIN_URL_KEY);
                if (saved && this.appRouter.url !== saved) {
                    sessionStorage.removeItem(ExtendedMsalGuard.POST_LOGIN_URL_KEY);
                    this.appRouter.navigateByUrl(saved);
                }
            });
    }

    public canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean | UrlTree> {
        if (!this.msalService.instance.getAllAccounts().length) {
            return this.handleAnonymousUser(state);
        }

        return super.canActivate(route, state);
    }

    public canActivateChild(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean | UrlTree> {
        if (!this.msalService.instance.getAllAccounts().length) {
            return this.handleAnonymousUser(state);
        }

        return super.canActivateChild(route, state);
    }

    private handleAnonymousUser(state: RouterStateSnapshot): Observable<UrlTree> {
        sessionStorage.setItem(ExtendedMsalGuard.POST_LOGIN_URL_KEY, state.url);

        // Preserve special payment outlets and fallback to OTP landing page.
        if (state.url.includes('(payment:payment-success)')) {
            return of(this.appRouter.parseUrl('/(payment:payment-success)'));
        }
        if (state.url.includes('(payment:payment-error)')) {
            return of(this.appRouter.parseUrl('/(payment:payment-error)'));
        }

        return of(this.appRouter.parseUrl('/'));
    }
}
